import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, Observable, of, throwError } from 'rxjs';
import { ConfigService } from 'src/app/core/services/config.service';
import { catchError, delay } from 'rxjs/operators';
import { WaiverAppealStatusModel } from 'src/app/shared/models/waiver-appeal-status.model';
import { WaiverFormModel } from 'src/app/shared/models/waiver-form.model';
import { StudentWaiverStatus } from '../../models/review-waivers/student-waiver-status';
import { StudentWaiverViewModel } from '../../models/student-waiver-view.model';
import { WaiverStatusModel } from 'src/app/shared/models/waiver-status.model';
import { CoverageStatusModel } from 'src/app/shared/models/coverage-status.model';
import { CoverageTypeModel } from 'src/app/shared/models/coverage-type.model';
import { APIEndPoints } from 'src/app/shared/APIEndPoints';
import { Common } from 'src/app/core/common/common';
import { ClientService } from 'src/app/core/services/client/client.service';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable({
  providedIn: 'root'
})
export class WaiversService {

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
    private clientService: ClientService
  ) { }

  waiverApiUrl = this.configService.waiverApiUri;

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    if (error)
      return throwError(error.error);
    else
      return throwError('Something bad happened; please try again later.');
  }

  getWaiverAppealStatuses(): Observable<WaiverAppealStatusModel[]> {
    const apiURL = this.waiverApiUrl + '/api/Common/WaiverAppealStatuses';
    return this.http.get<WaiverAppealStatusModel[]>(apiURL, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  // getIsStatusUpdateAllowed(userRole: string, currentStatus: string): Observable<any> {
  //   const apiURL = `this.waiverApiUrl + '/api/WaiverRequests/GetIsUserAllowedUpdateStatus/${userRole}/${currentStatus}`;
  //   return this.http.get<any>(apiURL,{})
  //     .pipe(
  //       catchError(this.handleError)
  //     );
  // }

  getWaiverForms(clientId: number, policyYear: number): Observable<WaiverFormModel[]> {
    const apiURL = this.waiverApiUrl + '/api/Clients/WaiverForms?clientId=' + clientId + '&policyYear=' + policyYear;
    return this.http.get<WaiverFormModel[]>(apiURL, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  getReviewWaiver(clientId: number,
    studentId: string,
    lastName: string,
    firstName: string,
    policyYear: number,
    email: string,
    waiverStatus: string,
    appealStatus: string,
    wiverForm: string,
    incInactiveStudents: boolean,
    campLoc: string,
    RowCount: number,
    PageNumber: number,
    SortColumn: string,
    SortDirection: string
  ): Observable<any> {
    const apiURL = this.waiverApiUrl + '/api/WaiverRequests/SearchForRewrite';
    return this.http.post<any>(apiURL,
      {
        'PolicyYear': policyYear,
        'ClientId': clientId,
        'StudentId': studentId,
        'LastName': lastName,
        'FirstName': firstName,
        'EmailAddress': email,
        'WaiverStatus': waiverStatus,
        'AppealStatus': appealStatus,
        'WaiverForm': wiverForm,
        'CampusLocation': campLoc,
        'IncludeInactive': incInactiveStudents,
        'RowCount': RowCount,
        'PageNumber': PageNumber,
        'SortColumn': SortColumn,
        'SortDirection': SortDirection,
      }, httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  getWaiverPeriodDetails(clientId: number, policyYear: number): Observable<any> {
    const apiURL = this.waiverApiUrl + '/api/Clients/WaiverPeriodDetails?clientId=' + clientId + '&policyYear=' + policyYear;
    return this.http.get<any>(apiURL, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  getWaiverFields(cardId: number, clientId: number) {
    const apiURL = this.waiverApiUrl + '/api/WaiverResponse/GetWaiverFields?cardId=' + cardId + '&clientId=' + clientId;
    return this.http.get<any>(apiURL, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  getAllinsuranceCompanyNames() {
    const apiURL = this.waiverApiUrl + '/api/Common/InsuranceCompanyNames';
    return this.http.get<any>(apiURL, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  getUserRoleSpecificWaiverStatusLIst(policyYear: number, clientId: number, roleId: number) {
    const apiURL = this.waiverApiUrl + '/api/WaiverRequests/GetUserRoleBasedWaiverStatus';
    return this.http.post<any>(apiURL, {
      UserRoleId: roleId,
      ClientId: clientId,
      PolicyYear: policyYear
    })
      .pipe(
        catchError(this.handleError)
      );
  }

  getStudentWaiverById(waiverResponseId: number, clientId: number, isEditable = false): Observable<StudentWaiverViewModel> {
    let apiURL = this.waiverApiUrl + `/api/WaiverResponse/GetStudentWaiverById?waiverResponseId=${waiverResponseId}&clientId=${clientId}&isEditable=${isEditable}`;
    apiURL = isEditable ? `${apiURL}&isEditable=true` : apiURL;
    return this.http.get<any>(apiURL, {})
      .pipe(
        catchError(this.handleError)
      );
  }

  updateWaiverResponse(body: any) {
    const apiURL = this.waiverApiUrl + '/api/WaiverResponse/UpdateWaiverResponse';
    return this.http.post<any>(apiURL, body)
      .pipe(
        catchError(this.handleError)
      );
  }
  public getEmailConfirmationInfo(body: any): Observable<any> {
    let url = this.waiverApiUrl + '/api/WaiverTemplates/WaiverEmailConformation';
    const confirmations = this.http.post<any>(url, body);
    return confirmations;
  }

  sendEmailConfirmationInfo(body: any): Observable<any> {
    const apiURL = this.waiverApiUrl + '/api/WaiverResponse/UpdateWaiverStatus';
    return this.http.post<string[]>(apiURL, body)
      .pipe(
        catchError(this.handleError)
      );
  }
  public getWaiverStatuses(policyYear: number, clientId: Number, clientNum: number): Observable<WaiverStatusModel[]> {

    let url = this.waiverApiUrl + `/api/Common/WaiverStatuses/${policyYear}/${clientId}/${clientNum}`;
    return this.http.get<WaiverStatusModel[]>(url, {});
  }

  public getWaiverStatusesforCard(policyYear: number, clientId: Number, cardId: Number): Observable<any> {

    let url = this.waiverApiUrl + `/api/WaiverStatus/WaiverStatuses/${policyYear}/${clientId}/${cardId}`;
    return this.http.get<any>(url, {});
  }
  public getCoverageStatuses(clientId: number): Observable<CoverageStatusModel[]> {
    let url = this.waiverApiUrl + '/api/Clients/' + clientId + '/CoverageStatuses';
    const statuses = this.http.get<CoverageStatusModel[]>(url, {});
    return statuses; {
    }
  }




  // public getCampusLocations(clientId: number) {
  //   const url = this.waiverApiUrl + '/api/Clients/' + clientId + '/ClientLocations';
  //   return this.http.get<any>(url);
  // }

  public getClientPolicyConfigurations(policyYear: number, clientNumber: number) {
    const url = this.waiverApiUrl + '/api/Policy/GetClientPolicyConfigurations?policyYear=' + policyYear + '&clientNumber=' + clientNumber;
    return this.http.get<any>(url);
  }
  // we have in security GetByClientId is the method
  public getClientCustomFields(clientId: number) {
    const url = this.waiverApiUrl + '/api/Clients/' + clientId + '/ClientCustomFields';
    return this.http.get<any>(url);
  }

  public validateStateAndZip(stateCode, zipCode) {
    if (!stateCode || !zipCode) {
      return of({ result: true });
    }
    if (zipCode.length === 5 || zipCode.length === 9) {

      const url = this.waiverApiUrl + '/api/Common/ValidateStateAndZip?stateCode=' + stateCode + '&zipCode=' + zipCode;
      return this.http.get<any>(url, { headers: { 'showLoader': 'false' } }).pipe(delay(400));
    }
  }
  public getCoverageTypes(): Observable<CoverageTypeModel[]> {
    let url = this.waiverApiUrl + '/api/Clients/CoverageTypes';
    const statuses = this.http.get<CoverageTypeModel[]>(url, {});
    return statuses; {
    }
  }

  public getWaiverConfirmations(clientId, campuslocationId, waiverCardId, waiverStatusText): Observable<any> {
    let url = this.waiverApiUrl + '/api/WaiverTemplates/WaiverCardTemplate?clientId=' + clientId + '&campusLocationId=' + campuslocationId + '&waiverCardId=' + waiverCardId + '&waiverStatusText=' + waiverStatusText;
    const confirmations = this.http.get<any>(url, {});
    return confirmations;
  }
  public getThirdPartyVerificationEnabledClientIds(): Observable<Number[]> {
    return this.http.get<Number[]>(`${this.waiverApiUrl}${APIEndPoints.getThirdPartyVerificationEnabledClientIds}`).pipe(
      catchError(Common.handleError)
    );
  }
  public getAppealEnabledClientIds(): Observable<Number[]> {
    return this.http.get<Number[]>(`${this.waiverApiUrl}${APIEndPoints.getAppealEnabledClientIds}`).pipe(
      catchError(Common.handleError)
    );
  }
  public getStates() {
    const url = this.waiverApiUrl + '/api/Common/States';
    return this.http.get<any>(url);
  }
  filterThirdPartyVerificationEnabledClients(allowedClients: any): void {
    this.getThirdPartyVerificationEnabledClientIds().subscribe({
      next: (res) => {
        if (res.length > 0) {
          this.clientService.createClientList(allowedClients, res);
        } 
      },
      error: (error) => {
        console.error(`error : ${error}`);
        //this.clientService.handlePolicySpinner(false);
      },
      complete: () => {
      }
    });
  }
}
